// ────────────────────────────────────────────────────────────────────────────
//  Shifts.tsx — Schichtplaner / Arbeitszeiteneinteilung
//
//  Zwei Modi je nach Berechtigung:
//   • Verwalten (Owner / can_manage_shifts): Wochen-Übersicht aller Schichten,
//     neue Schichten anlegen / bearbeiten / löschen, Mitarbeiter zuweisen
//     und Schichten freigeben. Nur sichtbar wenn ShiftPlanner-Add-On aktiv.
//   • Lesen (alle Org-Mitglieder): „Mein Plan" — eigene zukünftige Schichten,
//     accept/decline-Buttons, Detail-Sicht.
// ────────────────────────────────────────────────────────────────────────────

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useT, useLangCode } from '../i18n'
import { useOrg, useOrgMembers, type OrgMember } from '../lib/useOrg'
import { useExtensions } from '../lib/useExtensions'
import { useShifts, useMyShifts, type Shift, type MyShift } from '../lib/useShifts'
import { Icon } from '../components/Icon'

export function Shifts() {
  const t = useT()
  const { org, loading: orgLoading } = useOrg()
  const { has } = useExtensions()
  const [tab, setTab] = useState<'my' | 'manage'>('my')

  if (orgLoading) return <Spinner />
  if (!org) return <NoOrgLock t={t} />

  const hasShiftPlanner = has('ShiftPlanner')

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-10">
      <header className="mb-6 md:mb-8">
        <div className="inline-block text-[10px] font-bold tracking-widest text-accent uppercase mb-2">
          {t('shifts.kicker')}
        </div>
        <h1 className="text-3xl md:text-5xl font-bold tracking-tight heading-fade mb-2">
          {t('shifts.title')}
        </h1>
        <p className="text-app-text-dim max-w-2xl text-sm md:text-base">
          {t('shifts.sub')}
        </p>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 rounded-xl glass-flat w-fit">
        <TabButton active={tab === 'my'}     onClick={() => setTab('my')}     label={t('shifts.tabMy')}     />
        <TabButton active={tab === 'manage'} onClick={() => setTab('manage')} label={t('shifts.tabManage')} />
      </div>

      {tab === 'my'     && <MyShiftsView t={t} />}
      {tab === 'manage' && (hasShiftPlanner ? <ManageShiftsView t={t} /> : <UpsellLock t={t} />)}
    </div>
  )
}

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
        active ? 'bg-accent text-white shadow-md shadow-accent/30'
               : 'text-app-text-dim hover:text-app-text'
      }`}
    >
      {label}
    </button>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  MY SHIFTS — alle User
// ════════════════════════════════════════════════════════════════════════════
function MyShiftsView({ t }: { t: any }) {
  const lang = useLangCode()
  const { items, loading } = useMyShifts({
    from: new Date(Date.now() - 7 * 86400_000).toISOString(),
  })

  if (loading) return <Spinner />

  const upcoming = items.filter(i => new Date(i.endsAt).getTime() >= Date.now())
  const past     = items.filter(i => new Date(i.endsAt).getTime() <  Date.now())

  if (items.length === 0) {
    return (
      <div className="glass-strong rounded-3xl p-8 text-center">
        <div className="text-6xl mb-4 opacity-50">📅</div>
        <h3 className="text-xl font-bold mb-2">{t('shifts.myEmpty')}</h3>
        <p className="text-app-text-dim text-sm max-w-md mx-auto">{t('shifts.myEmptySub')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {upcoming.length > 0 && (
        <section>
          <h2 className="text-lg font-bold mb-3">{t('shifts.myUpcoming')} <span className="text-app-text-mute font-medium">({upcoming.length})</span></h2>
          <div className="grid md:grid-cols-2 gap-4">
            {upcoming.map(s => <MyShiftCard key={s.id} shift={s} lang={lang} t={t} />)}
          </div>
        </section>
      )}
      {past.length > 0 && (
        <section>
          <h2 className="text-lg font-bold mb-3 text-app-text-mute">{t('shifts.myPast')} <span className="font-medium">({past.length})</span></h2>
          <div className="grid md:grid-cols-2 gap-4 opacity-70">
            {past.slice(0, 6).map(s => <MyShiftCard key={s.id} shift={s} lang={lang} t={t} hidePast />)}
          </div>
        </section>
      )}
    </div>
  )
}

function MyShiftCard({
  shift, lang, t, hidePast,
}: {
  shift: MyShift; lang: 'de' | 'en'; t: any
  hidePast?: boolean
}) {
  const start = new Date(shift.startsAt)
  const end   = new Date(shift.endsAt)
  const ts = formatRange(start, end, lang)
  const dayLabel = formatDayLabel(start, lang, t)

  return (
    <div className="glass rounded-2xl p-5 relative overflow-hidden">
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: shift.color || '#8a44ee' }} />
      <div className="pl-3">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="text-[10px] font-bold tracking-wider uppercase text-app-text-mute">{dayLabel}</div>
          {!hidePast && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase"
                  style={{ background: 'rgba(138,68,238,0.15)', color: '#b89cff', border: '1px solid rgba(138,68,238,0.30)' }}>
              ✓ {t('shifts.assigned')}
            </span>
          )}
        </div>
        <h3 className="text-lg font-bold tracking-tight">{shift.title}</h3>
        <div className="text-sm text-app-text-dim mt-0.5">{ts}</div>
        {shift.location && (
          <div className="mt-2 text-xs text-app-text-mute">📍 {shift.location}</div>
        )}
        {shift.requiredRole && (
          <div className="mt-1 text-xs text-app-text-mute">👤 {shift.requiredRole}</div>
        )}
        {shift.description && (
          <p className="mt-2 text-sm text-app-text-dim leading-relaxed">{shift.description}</p>
        )}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  MANAGE SHIFTS — Owner / can_manage_shifts
// ════════════════════════════════════════════════════════════════════════════
function ManageShiftsView({ t }: { t: any }) {
  const lang = useLangCode()
  const { items: members } = useOrgMembers(true)
  const [weekOffset, setWeekOffset] = useState(0)
  const range = weekRange(weekOffset)
  const {
    items, canManage, loading,
    create, update, remove, publish, assign, unassign,
  } = useShifts({ from: range.from.toISOString(), to: range.to.toISOString() })

  const [editor, setEditor] = useState<{ open: boolean; shift?: Shift | null; default?: Date }>({ open: false })

  // ── Hooks MÜSSEN vor jedem early-return stehen — sonst „Rules of Hooks"-
  // Verletzung: React tracked Hooks per Aufruf-Reihenfolge, und ein early
  // `if (loading) return <Spinner />` würde sie beim Folge-Render fehlen
  // lassen → React crasht mit „Rendered more hooks than during the previous
  // render".

  // Tag-Spalten — useMemo darf keine if-Guards davor haben
  const days = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(range.from)
      d.setDate(d.getDate() + i)
      return d
    })
  }, [range.from])

  const groupedByDay = useMemo(() => {
    const m: Record<string, Shift[]> = {}
    days.forEach(d => { m[ymd(d)] = [] })
    items.forEach(s => {
      const k = ymd(new Date(s.startsAt))
      if (m[k]) m[k].push(s)
      else (m[k] = [s])
    })
    return m
  }, [items, days])

  // Early-Returns NACH allen Hooks (siehe Kommentar oben)
  if (loading) return <Spinner />
  if (!canManage) {
    return (
      <div className="glass-strong rounded-3xl p-6 md:p-8 text-center">
        <Icon name="lock" size={28} className="mx-auto text-app-text-mute mb-3" />
        <h3 className="text-lg font-bold mb-1">{t('shifts.noPermTitle')}</h3>
        <p className="text-app-text-dim text-sm">{t('shifts.noPermSub')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header-Bar mit Wochen-Switch + Add-Button */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <button onClick={() => setWeekOffset(w => w - 1)}
                  className="w-9 h-9 rounded-lg glass-flat hover:bg-app-elev-2 grid place-items-center transition">
            <Icon name="arrowLeft" size={16} />
          </button>
          <button onClick={() => setWeekOffset(0)}
                  className="px-3 h-9 rounded-lg glass-flat hover:bg-app-elev-2 text-xs font-semibold transition">
            {t('shifts.thisWeek')}
          </button>
          <button onClick={() => setWeekOffset(w => w + 1)}
                  className="w-9 h-9 rounded-lg glass-flat hover:bg-app-elev-2 grid place-items-center transition">
            <Icon name="arrowRight" size={16} />
          </button>
          <div className="ml-3 text-sm font-semibold">
            {formatWeekHeader(range.from, range.to, lang)}
          </div>
        </div>
        <button onClick={() => setEditor({ open: true })} className="btn-primary text-sm">
          <Icon name="plus" size={14} /> {t('shifts.add')}
        </button>
      </div>

      {/* Wochen-Grid */}
      <div className="glass-strong rounded-3xl overflow-hidden">
        <div className="grid grid-cols-7 text-[11px] font-bold uppercase tracking-wider text-app-text-mute border-b border-app-border">
          {days.map(d => (
            <div key={ymd(d)} className="px-3 py-3 text-center">
              <div>{weekdayShort(d, lang)}</div>
              <div className="text-app-text font-bold text-base mt-0.5">{d.getDate()}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 min-h-[360px]">
          {days.map(d => {
            const k = ymd(d)
            return (
              <div key={k}
                   className="px-2 py-2 border-r border-app-border last:border-r-0 space-y-1.5"
                   onDoubleClick={() => {
                     const at = new Date(d); at.setHours(8, 0, 0, 0)
                     setEditor({ open: true, default: at })
                   }}>
                {(groupedByDay[k] ?? []).map(s => (
                  <ShiftBlock key={s.id} shift={s} onClick={() => setEditor({ open: true, shift: s })} />
                ))}
              </div>
            )
          })}
        </div>
      </div>

      {/* Editor-Modal */}
      {editor.open && (
        <ShiftEditor
          t={t}
          members={members}
          initial={editor.shift ?? null}
          defaultStart={editor.default}
          onClose={() => setEditor({ open: false })}
          onSave={async (form, ids) => {
            if (editor.shift) {
              await update(editor.shift.id, form)
              // diff assignments
              const existing = new Set((editor.shift.assignments ?? []).map(a => a.userId))
              const next     = new Set(ids)
              const toAdd    = ids.filter(u => !existing.has(u))
              const toRemove = (editor.shift.assignments ?? []).filter(a => !next.has(a.userId))
              if (toAdd.length)    await assign(editor.shift.id, toAdd)
              for (const a of toRemove) await unassign(editor.shift.id, a.userId)
            } else {
              await create({ ...form, assignedUserIds: ids })
            }
            setEditor({ open: false })
          }}
          onPublish={async (id) => { await publish(id); setEditor({ open: false }) }}
          onDelete={async (id) => { await remove(id); setEditor({ open: false }) }}
        />
      )}
    </div>
  )
}

function ShiftBlock({ shift, onClick }: { shift: Shift; onClick: () => void }) {
  const start = new Date(shift.startsAt)
  const end   = new Date(shift.endsAt)
  const hh = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  const isPub = shift.status === 'published'
  return (
    <button onClick={onClick}
            className="block w-full text-left rounded-lg p-2 hover:scale-[1.02] transition group/sb"
            style={{
              background: shift.color ? `${shift.color}28` : 'rgba(138,68,238,0.18)',
              borderLeft: `3px solid ${shift.color || '#8a44ee'}`,
            }}>
      <div className="text-[10px] font-bold tracking-wider text-app-text-mute">
        {hh(start)} – {hh(end)} {!isPub && '· Entwurf'}
      </div>
      <div className="font-semibold text-sm truncate">{shift.title}</div>
      {shift.assignments.length > 0 && (
        <div className="mt-1 flex -space-x-1.5">
          {shift.assignments.slice(0, 4).map(a => (
            <div key={a.id}
                 title={a.displayName}
                 className="w-5 h-5 rounded-full border-2 border-app-bg grid place-items-center text-[8px] font-bold text-white"
                 style={{ background: 'linear-gradient(135deg,#8a44ee,#3a62ff)' }}>
              {(a.displayName || '?').split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase()}
            </div>
          ))}
          {shift.assignments.length > 4 && (
            <div className="w-5 h-5 rounded-full border-2 border-app-bg bg-app-elev grid place-items-center text-[8px] font-bold text-app-text-mute">
              +{shift.assignments.length - 4}
            </div>
          )}
        </div>
      )}
    </button>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  EDITOR-MODAL
// ════════════════════════════════════════════════════════════════════════════
function ShiftEditor({
  t, members, initial, defaultStart, onClose, onSave, onPublish, onDelete,
}: {
  t: any
  members: OrgMember[]
  initial:  Shift | null
  defaultStart?: Date
  onClose: () => void
  onSave: (form: Partial<Shift>, userIds: string[]) => Promise<void>
  onPublish: (id: number) => Promise<void>
  onDelete: (id: number) => Promise<void>
}) {
  const start0 = initial?.startsAt
    ? new Date(initial.startsAt)
    : (defaultStart ?? new Date(Math.ceil(Date.now() / 3600000) * 3600000))
  const end0 = initial?.endsAt
    ? new Date(initial.endsAt)
    : new Date(start0.getTime() + 8 * 3600 * 1000)

  const [title,    setTitle]    = useState(initial?.title ?? '')
  const [desc,     setDesc]     = useState(initial?.description ?? '')
  const [location, setLocation] = useState(initial?.location ?? '')
  const [color,    setColor]    = useState(initial?.color ?? '#8a44ee')
  const [requiredRole, setRequiredRole] = useState(initial?.requiredRole ?? '')
  const [breakMinutes, setBreakMinutes] = useState(initial?.breakMinutes ?? 0)
  const [startsAt, setStartsAt] = useState(toLocalInput(start0))
  const [endsAt,   setEndsAt]   = useState(toLocalInput(end0))
  const [userIds,  setUserIds]  = useState<string[]>(
    initial?.assignments?.map(a => a.userId) ?? []
  )
  const [busy, setBusy] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    setBusy(true)
    try {
      await onSave({
        title: title.trim(),
        description: desc,
        location,
        color,
        requiredRole,
        breakMinutes,
        startsAt: new Date(startsAt).toISOString(),
        endsAt:   new Date(endsAt).toISOString(),
      }, userIds)
    } catch (e: any) {
      alert(e?.message ?? 'save failed')
    } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4"
         style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)' }}
         onClick={onClose}>
      <div className="w-full max-w-xl glass-strong rounded-3xl p-6 md:p-7 max-h-[92vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-bold tracking-tight">
            {initial ? t('shifts.editTitle') : t('shifts.newTitle')}
          </h3>
          <button onClick={onClose} className="text-app-text-mute hover:text-app-text text-2xl">×</button>
        </header>

        <form onSubmit={submit} className="space-y-4">
          <Field label={t('shifts.fieldTitle')}>
            <input value={title} onChange={e => setTitle(e.target.value)} required
                   placeholder={t('shifts.fieldTitlePh')} className="input-base" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('shifts.fieldStart')}>
              <input type="datetime-local" value={startsAt} onChange={e => setStartsAt(e.target.value)}
                     className="input-base" required />
            </Field>
            <Field label={t('shifts.fieldEnd')}>
              <input type="datetime-local" value={endsAt} onChange={e => setEndsAt(e.target.value)}
                     className="input-base" required />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('shifts.fieldLocation')}>
              <input value={location} onChange={e => setLocation(e.target.value)}
                     placeholder={t('shifts.fieldLocationPh')} className="input-base" />
            </Field>
            <Field label={t('shifts.fieldRole')}>
              <input value={requiredRole} onChange={e => setRequiredRole(e.target.value)}
                     placeholder={t('shifts.fieldRolePh')} className="input-base" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('shifts.fieldBreak')}>
              <input type="number" min={0} max={480} value={breakMinutes}
                     onChange={e => setBreakMinutes(parseInt(e.target.value || '0', 10))}
                     className="input-base" />
            </Field>
            <Field label={t('shifts.fieldColor')}>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map(c => (
                  <button key={c} type="button"
                          onClick={() => setColor(c)}
                          aria-label={c}
                          className={`w-8 h-8 rounded-lg border-2 transition ${color === c ? 'border-app-text scale-110' : 'border-transparent'}`}
                          style={{ background: c }} />
                ))}
              </div>
            </Field>
          </div>

          <Field label={t('shifts.fieldDesc')}>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={3}
                      placeholder={t('shifts.fieldDescPh')}
                      className="input-base resize-none" />
          </Field>

          <Field label={t('shifts.fieldAssign')}>
            <MemberPicker members={members} selected={userIds} onChange={setUserIds} t={t} />
          </Field>

          {/* Buttons */}
          <div className="flex flex-wrap gap-2 pt-3 border-t border-app-border">
            {initial && (
              <>
                <button type="button" disabled={busy}
                        onClick={() => { if (window.confirm(t('shifts.deleteConfirm') as string)) onDelete(initial.id) }}
                        className="px-4 py-2 rounded-xl text-sm font-semibold bg-coral/15 text-coral border border-coral/30 hover:bg-coral/25 transition">
                  {t('common.delete')}
                </button>
                {initial.status !== 'published' && (
                  <button type="button" disabled={busy}
                          onClick={() => onPublish(initial.id)}
                          className="px-4 py-2 rounded-xl text-sm font-semibold bg-teal/15 text-teal border border-teal/30 hover:bg-teal/25 transition">
                    📣 {t('shifts.publish')}
                  </button>
                )}
              </>
            )}
            <div className="flex-1" />
            <button type="button" onClick={onClose} disabled={busy}
                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-app-elev hover:bg-app-elev-2 transition">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={busy || !title.trim()} className="btn-primary px-5 py-2 text-sm">
              {busy ? '…' : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const COLORS = ['#8a44ee', '#3a62ff', '#33d670', '#ff5b5c', '#ffb300', '#06b6d4']

function MemberPicker({
  members, selected, onChange, t,
}: { members: OrgMember[]; selected: string[]; onChange: (ids: string[]) => void; t: any }) {
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id])
  if (!members.length) {
    return <div className="text-sm text-app-text-mute">{t('shifts.noMembers')}</div>
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-44 overflow-y-auto pr-1">
      {members.map(m => {
        const on = selected.includes(m.userId)
        const initials = (m.displayName || '?').split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
        return (
          <button key={m.userId} type="button" onClick={() => toggle(m.userId)}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition ${
                    on ? 'bg-accent/20 border border-accent/40' : 'bg-app-elev hover:bg-app-elev-2 border border-transparent'
                  }`}>
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-accent to-brandBlue grid place-items-center text-[9px] font-bold text-white">
              {initials || '?'}
            </div>
            <span className="truncate">{m.displayName}</span>
            {on && <Icon name="check" size={10} className="ml-auto text-accent" />}
          </button>
        )
      })}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  Upsell- + No-Org-Lock
// ════════════════════════════════════════════════════════════════════════════
function UpsellLock({ t }: { t: any }) {
  return (
    <div className="relative">
      <div className="absolute inset-0 rounded-[28px] blur-2xl opacity-50 pointer-events-none"
           style={{ background: 'linear-gradient(135deg,#3a62ff,#8a44ee)' }} />
      <div className="relative glass-strong rounded-[28px] p-8 md:p-10 text-center">
        <div className="text-5xl mb-4">🗓</div>
        <h3 className="text-2xl font-bold mb-2">{t('shifts.upsellTitle')}</h3>
        <p className="text-app-text-dim text-sm max-w-md mx-auto mb-6">{t('shifts.upsellSub')}</p>
        <Link to="/app/shop" className="btn-primary inline-flex">
          ✨ {t('shifts.upsellCta')}
        </Link>
      </div>
    </div>
  )
}

function NoOrgLock({ t }: { t: any }) {
  return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center">
      <div className="glass-strong p-8 md:p-10">
        <div className="text-6xl mb-4">🔒</div>
        <h1 className="text-2xl font-bold mb-2">{t('shifts.noOrgTitle')}</h1>
        <p className="text-app-text-dim leading-relaxed mb-6">{t('shifts.noOrgSub')}</p>
        <Link to="/app/org" className="btn-primary">🏢 {t('shifts.noOrgCta')}</Link>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  Helper-Components & Funktionen
// ════════════════════════════════════════════════════════════════════════════
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold text-app-text-dim uppercase tracking-wider mb-1.5">{label}</span>
      {children}
    </label>
  )
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-10 h-10 rounded-full border-4 border-app-border border-t-accent animate-spin" />
    </div>
  )
}

function weekRange(offsetWeeks: number): { from: Date; to: Date } {
  const now = new Date()
  const d = new Date(now)
  // Mo als erster Tag (DE/EU-Style)
  const day = (d.getDay() + 6) % 7  // 0 = Mon
  d.setDate(d.getDate() - day + offsetWeeks * 7)
  d.setHours(0, 0, 0, 0)
  const from = d
  const to   = new Date(d)
  to.setDate(to.getDate() + 7)
  return { from, to }
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function weekdayShort(d: Date, lang: 'de' | 'en'): string {
  return d.toLocaleDateString(lang, { weekday: 'short' })
}

function formatWeekHeader(from: Date, to: Date, lang: 'de' | 'en'): string {
  const a = from.toLocaleDateString(lang, { day: '2-digit', month: 'short' })
  const e = new Date(to); e.setDate(e.getDate() - 1)
  const b = e.toLocaleDateString(lang, { day: '2-digit', month: 'short', year: 'numeric' })
  return `${a} – ${b}`
}

function formatRange(start: Date, end: Date, lang: 'de' | 'en'): string {
  const t = (d: Date) => d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })
  return `${t(start)} – ${t(end)}`
}

function formatDayLabel(d: Date, lang: 'de' | 'en', t: any): string {
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1)
  const dd = new Date(d); dd.setHours(0, 0, 0, 0)
  if (dd.getTime() === today.getTime())    return t('shifts.today')
  if (dd.getTime() === tomorrow.getTime()) return t('shifts.tomorrow')
  return d.toLocaleDateString(lang, { weekday: 'long', day: '2-digit', month: 'short' })
}

/** Convert Date → YYYY-MM-DDTHH:mm string for <input type="datetime-local">. */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
